import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useLang } from "@/contexts/LangContext";
import ProjectFormModal from "./ProjectFormModal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TRANSLATION_LANGS = ["en", "ru", "uk"];
const TRANSLATION_LANG_LABELS = { en: "EN", ru: "RU", uk: "UK" };

function isLangTranslated(project, lang) {
  return Boolean(project[`short_description_${lang}`] && project[`description_${lang}`]);
}

function TranslationBadges({ project }) {
  return (
    <div className="inline-flex items-center gap-1.5" data-testid={`admin-project-translation-badges-${project.slug}`}>
      {TRANSLATION_LANGS.map((lang) => {
        const done = isLangTranslated(project, lang);
        return (
          <span
            key={lang}
            title={done ? `${TRANSLATION_LANG_LABELS[lang]} übersetzt` : `${TRANSLATION_LANG_LABELS[lang]} fehlt noch`}
            data-testid={`admin-project-translation-badge-${project.slug}-${lang}`}
            className={`px-1.5 py-0.5 text-[9px] tracking-[0.1em] uppercase border ${
              done ? "border-emerald-400/40 text-emerald-300 bg-emerald-400/10" : "border-white/15 text-white/35"
            }`}
          >
            {TRANSLATION_LANG_LABELS[lang]}
          </span>
        );
      })}
    </div>
  );
}

export default function ProjectsTab() {
  const { authHeaders } = useAuth();
  const { t } = useLang();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null = closed, {} = new, {...} = edit
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/projects`);
      setProjects(data);
    } catch (err) {
      console.warn("Projects load failed:", err?.response?.status ?? err?.message);
      toast.error(t("admin.projects.load_failed"));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    load();
  }, [load]);

  const handleSave = async (payload, id) => {
    setSaving(true);
    try {
      if (id) {
        await axios.put(`${API}/projects/${id}`, payload, { headers: authHeaders(), withCredentials: true });
      } else {
        await axios.post(`${API}/projects`, payload, { headers: authHeaders(), withCredentials: true });
      }
      toast.success(t("admin.projects.save_success"));
      setEditing(null);
      load();
    } catch (err) {
      console.warn("Project save failed:", err?.response?.status ?? err?.message);
      toast.error(err?.response?.data?.detail || t("admin.projects.save_failed"));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm(t("admin.projects.confirm_delete"))) return;
    try {
      await axios.delete(`${API}/projects/${id}`, { headers: authHeaders(), withCredentials: true });
      setProjects((s) => s.filter((p) => p.id !== id));
      toast.success(t("admin.projects.deleted"));
    } catch (err) {
      console.warn("Project delete failed:", err?.response?.status ?? err?.message);
      toast.error(t("admin.projects.delete_failed"));
    }
  };

  return (
    <div data-testid="admin-projects-tab">
      <div className="flex items-center justify-between mb-8">
        <h2 className="font-display text-2xl md:text-3xl font-light uppercase tracking-tight">
          {t("admin.projects.title")}
        </h2>
        <button
          onClick={() => setEditing({})}
          data-testid="admin-project-new-btn"
          className="inline-flex items-center gap-2 px-5 py-3 bg-white text-[#051630] text-xs tracking-[0.18em] uppercase font-bold hover:bg-transparent hover:text-white border border-white transition-colors"
        >
          <Plus size={14} strokeWidth={2} /> {t("admin.projects.new")}
        </button>
      </div>

      {loading ? (
        <div className="py-16 text-center text-white/50">…</div>
      ) : projects.length === 0 ? (
        <div className="border border-white/10 p-16 text-center">
          <p className="text-white/60">{t("admin.projects.empty")}</p>
        </div>
      ) : (
        <div className="border border-white/10 overflow-x-auto">
          <table className="w-full text-sm" data-testid="admin-projects-table">
            <thead className="border-b border-white/10">
              <tr className="text-[10px] tracking-[0.25em] uppercase text-white/50">
                <th className="text-left p-4 font-medium">{t("admin.projects.col_title")}</th>
                <th className="text-left p-4 font-medium hidden md:table-cell">{t("admin.projects.col_location")}</th>
                <th className="text-left p-4 font-medium hidden lg:table-cell">{t("admin.projects.col_status")}</th>
                <th className="text-left p-4 font-medium hidden lg:table-cell">{t("admin.projects.col_year")}</th>
                <th className="text-left p-4 font-medium hidden md:table-cell">{t("admin.projects.col_translations")}</th>
                <th className="text-right p-4 font-medium">{t("admin.projects.col_action")}</th>
              </tr>
            </thead>
            <tbody>
              {projects.map((p) => (
                <tr key={p.id} data-testid={`admin-project-row-${p.slug}`} className="border-b border-white/5 hover:bg-white/[0.02]">
                  <td className="p-4 text-white">{p.title}</td>
                  <td className="p-4 text-white/60 hidden md:table-cell">{p.location}</td>
                  <td className="p-4 text-white/60 hidden lg:table-cell">{p.status}</td>
                  <td className="p-4 text-white/60 hidden lg:table-cell">{p.year}</td>
                  <td className="p-4 hidden md:table-cell">
                    <TranslationBadges project={p} />
                  </td>
                  <td className="p-4 text-right">
                    <div className="inline-flex items-center gap-2">
                      <button
                        onClick={() => setEditing(p)}
                        data-testid={`admin-project-edit-${p.slug}`}
                        className="inline-flex items-center justify-center w-8 h-8 border border-white/15 hover:border-white hover:bg-white hover:text-[#051630] transition-colors"
                        aria-label="Edit"
                      >
                        <Pencil size={14} strokeWidth={1.5} />
                      </button>
                      <button
                        onClick={() => handleDelete(p.id)}
                        data-testid={`admin-project-delete-${p.slug}`}
                        className="inline-flex items-center justify-center w-8 h-8 border border-white/15 hover:border-white hover:bg-white hover:text-[#051630] transition-colors"
                        aria-label="Delete"
                      >
                        <Trash2 size={14} strokeWidth={1.5} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {editing !== null && (
        <ProjectFormModal
          project={editing.id ? editing : null}
          onClose={() => setEditing(null)}
          onSave={handleSave}
          saving={saving}
        />
      )}
    </div>
  );
}
